import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
// Models
import { RequestIndicatorTypes } from 'data-layer-cr/dist/models';
import {
  EmissionDocument,
  MenuDownloadProps,
} from 'src/models/emission/EmissionDocument';
// Redux
import { connect, ConnectedProps } from 'react-redux';
import {
  downloadPolicyFiles,
  resetPolicyState,
  restartDownloadFromMenu,
  unsetShowDownloadMenuStatus,
} from 'src/store/policy/actions';
import { RootState } from 'src/store/rootReducer';
// Components
import { Button, Snackbar, Typography } from '@material-ui/core';
import { colors } from 'design-system';
// Functions
import { getStartDocument } from 'src/functions/fileManagerMethods';
import {
  createEmptyFileList,
  createFileList,
  FileDownloadItem,
} from './FileHandlerHelpers';

function downloadFile(fileBase64: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = fileBase64;
  link.setAttribute('download', fileName);
  link.setAttribute('target', '_blank');
  document.body.appendChild(link);
  link.click();
}

export const FileHandler = ({
  menuDownloadOptions,
  downloadStatus,
  fileData,
  showDownloadMenuStatus,
  downloadPolicyFilesAction,
  resetPolicyStateAction,
  restartDownloadFromMenuAction,
  closeSnackbarAction,
}: Props): JSX.Element => {
  const cancelTokenSource = useRef(axios.CancelToken.source()).current;
  const [fileList, setFileList] = useState<FileDownloadItem[]>(
    createEmptyFileList(),
  );

  // observer when the menu button is clicked
  useEffect(() => {
    if (menuDownloadOptions.startDownload) {
      setFileList(createFileList(menuDownloadOptions));
      restartDownloadFromMenuAction();
      downloadPolicyFilesAction(
        menuDownloadOptions.quotationHash,
        getStartDocument(menuDownloadOptions.createdAt),
        cancelTokenSource,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuDownloadOptions]);

  // observes when file is successfully downloaded and delivers it to the client
  useEffect(() => {
    if (downloadStatus === 'initial' && fileList.length > 0) {
      const { fileName, quotationHash } = fileList[0];
      downloadPolicyFilesAction(quotationHash, fileName, cancelTokenSource);
      setFileList(fileList.slice(1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadStatus]);

  // observes when file is successfully downloaded and delivers it to the client
  useEffect(() => {
    if (downloadStatus === 'success') {
      downloadFile(fileData.content, fileData.name);
      resetPolicyStateAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadStatus]);

  // cleanup list on unmount
  useEffect(
    () => {
      return (): void => {
        resetPolicyStateAction();
        cancelTokenSource.cancel();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Snackbar
      data-testid="snackbar"
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      autoHideDuration={10000}
      open={showDownloadMenuStatus}
      message={
        <Typography variant="caption" style={{ color: colors.white }}>
          Descargando documentos
        </Typography>
      }
      onClose={closeSnackbarAction}
      action={
        <Button
          data-testid="close-button"
          size="small"
          onClick={closeSnackbarAction}
          style={{ color: colors.bright_turquoise }}
        >
          cerrar
        </Button>
      }
    />
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  downloadStatus: RequestIndicatorTypes;
  menuDownloadOptions: MenuDownloadProps;
  fileData: EmissionDocument;
  showDownloadMenuStatus: boolean;
} => ({
  fileData: state.policy.fileData,
  downloadStatus: state.policy.downloadStatus,
  menuDownloadOptions: state.policy.menuDownloadOptions,
  showDownloadMenuStatus: state.policy.showDownloadMenuStatus,
});

const reduxActions = {
  downloadPolicyFilesAction: downloadPolicyFiles,
  resetPolicyStateAction: resetPolicyState,
  restartDownloadFromMenuAction: restartDownloadFromMenu,
  closeSnackbarAction: unsetShowDownloadMenuStatus,
};

const connector = connect(mapStateToProps, reduxActions);
type Props = ConnectedProps<typeof connector>;

export default connector(FileHandler);
